import React, { useCallback, useState } from 'react';
import { TabsContent, ConfirmationDialog } from '../../../components/ui';
import { DocumentTextIcon, FileIcon } from '../../../components/ui/Icons';
import { CustomSplitViewWithTabs } from '../../../components/generic/SplitViewWithTabs';
import { useFlexibleList } from '../../../hooks/useSWR';
import { mutate } from 'swr';
import { API_BASE_URL } from '../../../config/apiConfig';
import { REVISION_ENTITY_TYPE_ID } from './myProceduresConfig';
import { mapProcedureAssignmentFromApi } from './mapProcedureAssignment';
import MyProceduresDetailsTab from './components/MyProceduresDetailsTab';
import MyProceduresFilesTab from './components/MyProceduresFilesTab';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const savedUser = localStorage.getItem('user');
  let userId = null;
  if (savedUser) {
    try {
      userId = JSON.parse(savedUser).id?.toString();
    } catch {
      userId = null;
    }
  }
  return {
    'Content-Type': 'application/json',
    ...(userId && { 'user-id': userId }),
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const CustomMyProceduresSplitView = ({
  isOpen,
  selectedRecord,
  config,
  fields,
  labels,
  onClose,
  onRecordUpdate,
}) => {
  const revisionId =
    selectedRecord?.canView !== false ? selectedRecord?.currentRevisionId : null;

  const { data: revisionFiles = [] } = useFlexibleList(
    revisionId ? `/file-attachment/entity/${REVISION_ENTITY_TYPE_ID}/${revisionId}` : null
  );

  const activeFiles = (revisionFiles || []).filter((f) => !f.isDeleted);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [ackError, setAckError] = useState(null);
  const [ackConfirmOpen, setAckConfirmOpen] = useState(false);

  const isPending =
    selectedRecord?.canView !== false &&
    selectedRecord?.viewStatus !== 'VIEWED';

  const acknowledgeProcedure = useCallback(async () => {
    if (!selectedRecord?.assignmentId || selectedRecord.canView === false) return;
    if (selectedRecord.viewStatus === 'VIEWED') return;

    setIsAcknowledging(true);
    setAckError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/procedure-assignment/me/${selectedRecord.assignmentId}/view`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to acknowledge procedure');
      }
      const dto = await res.json();
      const mapped = mapProcedureAssignmentFromApi(dto);
      await mutate('/procedure-assignment/me');
      setAckConfirmOpen(false);
      if (onRecordUpdate) {
        onRecordUpdate(mapped);
      }
    } catch (e) {
      setAckError(e.message || 'Failed to acknowledge');
    } finally {
      setIsAcknowledging(false);
    }
  }, [selectedRecord, onRecordUpdate]);

  const handleConfirmAcknowledge = () => {
    acknowledgeProcedure();
  };

  const tabs = [
    {
      value: 'details',
      label: 'Details',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      badge: null,
    },
    {
      value: 'files',
      label: 'Files',
      icon: <FileIcon className="w-4 h-4" />,
      badge: selectedRecord?.canView !== false ? activeFiles.length : null,
      badgeColor: 'bg-blue-600',
    },
  ];

  const revisionLabel =
    selectedRecord?.currentRevisionDetail?.version ||
    selectedRecord?.currentRevision ||
    '—';

  return (
    <>
    <CustomSplitViewWithTabs
      isOpen={isOpen}
      config={config}
      labels={labels}
      headerTitle={
        selectedRecord
          ? `${selectedRecord.code || ''} — ${selectedRecord.title || selectedRecord.id}`
          : labels.noSelectionText
      }
      selectedRecord={selectedRecord}
      emptyMessage={labels.noSelectionText}
      tabs={tabs}
      onClose={onClose}
      defaultTab="details"
    >
      <TabsContent value="details" className="mt-0 flex-1 flex flex-col min-h-0">
        <MyProceduresDetailsTab
          selectedRecord={selectedRecord}
          fields={fields}
          config={config}
          isPending={isPending}
          isAcknowledging={isAcknowledging}
          ackError={ackError}
          onAcknowledge={() => setAckConfirmOpen(true)}
        />
      </TabsContent>
      <TabsContent value="files" className="mt-0 flex-1 flex flex-col min-h-0">
        <MyProceduresFilesTab
          files={activeFiles}
          revisionVersion={selectedRecord?.currentRevision}
          canView={selectedRecord?.canView !== false}
        />
      </TabsContent>
    </CustomSplitViewWithTabs>

    <ConfirmationDialog
      isOpen={ackConfirmOpen}
      onClose={() => {
        if (!isAcknowledging) setAckConfirmOpen(false);
      }}
      onConfirm={handleConfirmAcknowledge}
      title="Επιβεβαίωση διαδικασίας"
      message="Επιβεβαιώνετε ότι διαβάσατε την τρέχουσα δημοσιευμένη έκδοση της διαδικασίας;"
      details={
        selectedRecord
          ? {
              Κωδικός: selectedRecord.code || '—',
              Τίτλος: selectedRecord.title || '—',
              Έκδοση: revisionLabel,
            }
          : null
      }
      warningMessage="Η ενέργεια καταγράφεται και δεν μπορεί να αναιρεθεί από εσάς."
      confirmText="Επιβεβαίωση"
      cancelText="Ακύρωση"
      confirmVariant="default"
      isLoading={isAcknowledging}
    />
    </>
  );
};

export default CustomMyProceduresSplitView;
